import { degrees, PDFDocument, PDFImage } from "pdf-lib";

import DocumentCategoryList from "issara-sdk/apis/DocumentCategoryList_core";
import DocumentTypeList from "issara-sdk/apis/DocumentTypeList_core";

import EmployeeDocumentDetailAPI from "issara-sdk/apis/EmployeeDocumentDetailAPI_users";
import EmployeeDocumentListCreateAPIM from "issara-sdk/apis/EmployeeDocumentListCreateAPI_usersM";

import EmployeeDocumentSerializer from "issara-sdk/types/EmployeeDocumentSerializer_users";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  FilterCriteriaType,
  filterItemsByCriteria,
  GenerateActionTypes,
  generatePrefixedActions,
  mapOptions,
  OptionType,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

/** ============== Type-Defining Constants =============  */
export const ACTIONS = {
  INIT: "INIT",
  CHANGE_FILTER: "CHANGE_FILTER",
  DELETE: "DELETE",
  SEARCH: "SEARCH",
  SELECT: "SELECT",
  UPLOAD: "UPLOAD",
} as const;

export const SEQUENCE_NAME = "EmployeeDocument" as const;

/** =============== Types and Interfaces ===============  */
export type State = Partial<{
  EmployeeDocumentSequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    cloneItems: EmployeeDocumentSerializer[];
    documentFilterList: string[];
    documentType: number | null;
    items: EmployeeDocumentSerializer[];
    selectedDocument: EmployeeDocumentSerializer | null;
    documentTypeOptions: OptionType[];
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "masterOptions" | "selectedEmployee"
>;

export type Event =
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "RunSequence"; params: Record<string, unknown> };

export type Data = {
  device?: number;
  division?: number;
};

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.CHANGE_FILTER]: {
    key?: "documentFilterList";
    value?: string[];
  };
  [ACTIONS.DELETE]: { id: number; onSuccess: () => void };
  [ACTIONS.SELECT]: { data: EmployeeDocumentSerializer };
  [ACTIONS.UPLOAD]: { files: FileList };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

/** ===================== Constants ====================  */
export const StateInitial: State = {
  EmployeeDocumentSequence: null,
};

export const DataInitial: Data = {};

const Masters = [["documentType", {}]] as const;

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

const EMP_DOC_CODE = "EMPDOC";

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  controller.handleEvent({ message: "GetMasterData", params: { masters: Masters } });

  controller.setState(
    { EmployeeDocumentSequence: { ...state.EmployeeDocumentSequence, sequenceIndex: "Action" } },
    () => {
      Action(controller, { ...params, action: ACTIONS.INIT });
      Action(controller, { ...params, action: ACTIONS.SEARCH });
    }
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.INIT]: HandleInit,
    [ACTIONS.CHANGE_FILTER]: HandleChangeFilter,
    [ACTIONS.DELETE]: HandleDelete,
    [ACTIONS.SEARCH]: HandleSearch,
    [ACTIONS.SELECT]: HandleSelect,
    [ACTIONS.UPLOAD]: HandleUpload,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    handler(controller, params);
  } else {
    console.error(`No handler found for action: ${action}`);
  }
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleInit: Handler = async (controller) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.INIT]: BLC_STATUS.LOADING },
  });

  const category = await GetDocumentCategoryDefault(controller);

  const [result] = await DocumentTypeList.list({
    apiToken: controller.apiToken,
    params: { category, limit: 99999 },
  });

  state = controller.getState();

  controller.setState({
    EmployeeDocumentSequence: {
      ...state.EmployeeDocumentSequence,
      documentTypeOptions: mapOptions(result?.items || []),
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.INIT]: null },
  });
};

const HandleSearch: Handler<Params<Actions["SEARCH"]>, Promise<void>> = async (
  controller,
  params
) => {
  let state = controller.getState();

  const [result] = await EmployeeDocumentListCreateAPIM.list({
    apiToken: controller.apiToken,
    employee_id: state.selectedEmployee?.id,
  });

  state = controller.getState();

  const items: EmployeeDocumentSerializer[] = result?.items || [];

  let selectedDocument = state.EmployeeDocumentSequence?.selectedDocument ?? null;

  if (selectedDocument) {
    const foundDoc = items.find((item) => item.id === selectedDocument?.id);

    if (!foundDoc) {
      selectedDocument = null;
    }
  }

  const filterItems = items
    .map((item) => item.document_name)
    .filter((value): value is string => !!value);

  controller.setState(
    {
      EmployeeDocumentSequence: {
        ...state.EmployeeDocumentSequence,
        cloneItems: [...items],
        documentFilterList: filterItems,
        selectedDocument,
      },
    },
    () => {
      HandleChangeFilter(controller, { ...params, action: ACTIONS.CHANGE_FILTER });
    }
  );
};

const HandleUpload: Handler<Params<Actions["UPLOAD"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPLOAD]: BLC_STATUS.LOADING },
  });

  const documentTypeOptions: Record<string, any>[] = state.masterOptions?.documentType || [];
  const foundDocType = documentTypeOptions.find(
    (option) => option.value === state.EmployeeDocumentSequence?.documentType
  );

  const promiseArr = [...params.files].map(
    async (file) =>
      EmployeeDocumentListCreateAPIM.create({
        apiToken: controller.apiToken,
        data: { document_name: foundDocType?.text || "", file } as any,
        employee_id: state.selectedEmployee?.id,
      }) as Promise<[EmployeeDocumentSerializer, unknown]>
  );

  const response = await Promise.all(promiseArr);

  const isError = response.some((item: any) => item[1]);

  if (isError) {
    const errors = response.map((item) => item[1]).filter(Boolean);

    SetErrorMessage(controller, { ...params, error: errors });

    await HandleSearch(controller, { ...params, action: ACTIONS.SEARCH });

    controller.setState({
      buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPLOAD]: null },
    });

    return;
  }

  await HandleSearch(controller, { ...params, action: ACTIONS.SEARCH });

  state = controller.getState();

  controller.setState({
    EmployeeDocumentSequence: {
      ...state.EmployeeDocumentSequence,
      documentType: null,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.UPLOAD]: null },
  });
};

const HandleDelete: Handler<Params<Actions["DELETE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE]: BLC_STATUS.LOADING },
  });

  const [, error] = await EmployeeDocumentDetailAPI.delete({
    apiToken: controller.apiToken,
    employee_id: state.selectedEmployee?.id,
    pk: params.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, error });

    return;
  }

  await HandleSearch(controller, { ...params, action: ACTIONS.SEARCH });

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE]: null },
  });

  params.onSuccess();
};

const HandleSelect: Handler<Params<Actions["SELECT"]>> = async (controller, params) => {
  const state = controller.getState();
  const file = params.data.file;

  if (file.includes(".pdf")) {
    controller.setState({
      EmployeeDocumentSequence: {
        ...state.EmployeeDocumentSequence,
        selectedDocument: params.data,
      },
    });

    return;
  }

  try {
    const response = await fetch(file).then(async (res) => {
      if (!res.ok) {
        throw new Error(`Failed to fetch file: ${res.status}`);
      }

      return res.arrayBuffer();
    });

    if (response.byteLength === 0) {
      throw new Error("File is empty");
    }

    const pdfDataUrl = await createPdfFromImage(response);

    controller.setState({
      EmployeeDocumentSequence: {
        ...state.EmployeeDocumentSequence,
        selectedDocument: {
          ...params.data,
          file: pdfDataUrl,
        },
      },
    });
  } catch (error) {
    console.error("Failed to process image:", error);
  }
};

const HandleChangeFilter: Handler<Params<Actions["CHANGE_FILTER"]>> = async (
  controller,
  params
) => {
  const state = controller.getState();

  const seq = state.EmployeeDocumentSequence ?? {};

  if (params.key && params.value) {
    seq[params.key] = params.value;
  }

  const patientFilterCriteria: FilterCriteriaType = {
    document_name: {
      allowedValues: seq.documentFilterList ?? [],
      operator: "eq",
    },
    items: seq.cloneItems ?? [],
  };

  const filtered = filterItemsByCriteria(patientFilterCriteria) as EmployeeDocumentSerializer[];

  controller.setState({ EmployeeDocumentSequence: { ...seq, items: filtered } });
};

/** ====================================================  */
/**                         APIS                          */
/** ====================================================  */
const GetDocumentCategoryDefault: Handler<unknown, Promise<number | undefined>> = async (
  controller
) => {
  const [result] = await DocumentCategoryList.list({
    apiToken: controller.apiToken,
    params: {
      limit: 999999,
    },
  });

  const items: Record<string, any>[] = result?.items || [];
  const foundCategory = items.find((item) => item.code === EMP_DOC_CODE);

  return foundCategory?.id as number | undefined;
};

/** ====================================================  */
/**                         Utils                         */
/** ====================================================  */
const createPdfFromImage = async (imageBuffer: ArrayBuffer): Promise<string> => {
  const pdfDoc = await PDFDocument.create();

  // ตรวจสอบประเภทไฟล์จาก signature
  const uint8Array = new Uint8Array(imageBuffer);
  const isJPEG = uint8Array[0] === 0xff && uint8Array[1] === 0xd8;
  const isPNG =
    uint8Array[0] === 0x89 &&
    uint8Array[1] === 0x50 &&
    uint8Array[2] === 0x4e &&
    uint8Array[3] === 0x47;

  let imageEmbed: PDFImage;

  if (isPNG) {
    console.log("Embedding as PNG...");
    imageEmbed = await pdfDoc.embedPng(imageBuffer);
  } else if (isJPEG) {
    console.log("Embedding as JPEG...");
    imageEmbed = await pdfDoc.embedJpg(imageBuffer);
  } else {
    throw new Error("Unsupported image format");
  }

  const imageDims = imageEmbed.scale(0.5);
  const page = pdfDoc.addPage([imageDims.width, imageDims.height]);

  page.drawImage(imageEmbed, {
    height: imageDims.height,
    opacity: 1,
    rotate: degrees(0),
    width: imageDims.width,
    x: 0,
    y: 0,
  });

  const base64 = await pdfDoc.saveAsBase64();

  return `data:application/pdf;base64,${base64}`;
};
